/**
 * Role hooks based on the currently selected tenant.
 *
 * Roles are derived from `currentTenant.role` in TenantContext,
 * which comes from the Keto-resolved tenant memberships returned by /auth/me.
 *
 * Role hierarchy:
 *   owner  → has: admin, owner
 *   admin  → has: admin
 *   member → has: (none of the privileged roles)
 *
 * Usage:
 *   const isAdmin = useHasRole('admin');       // true for owner & admin
 *   const isOwner = useHasRole('owner');        // true only for owner
 *   const canManage = useHasAnyRole(['admin', 'owner']);
 */

import { useTenant } from '@/contexts/TenantContext';

/**
 * Map a tenant membership role to the set of logical roles it grants.
 *
 * - 'owner'  → ['admin', 'owner']   (owners are implicitly admins)
 * - 'admin'  → ['admin']
 * - 'member' → []
 */
function resolveRoles(tenantRole: string | undefined): string[] {
  switch (tenantRole) {
    case 'owner':
      return ['admin', 'owner'];
    case 'admin':
      return ['admin'];
    default:
      return [];
  }
}

/**
 * Check if the user has a specific role in the currently selected tenant.
 *
 * @example
 *   const isAdmin = useHasRole('admin'); // true for owner & admin
 */
export function useHasRole(role: string): boolean {
  const { currentTenant } = useTenant();
  const roles = resolveRoles(currentTenant?.role);
  return roles.includes(role);
}

/**
 * Check if the user has any of the specified roles in the current tenant.
 *
 * @example
 *   const canManage = useHasAnyRole(['admin', 'owner']);
 */
export function useHasAnyRole(roles: string[]): boolean {
  const { currentTenant } = useTenant();
  const resolved = resolveRoles(currentTenant?.role);
  return roles.some(role => resolved.includes(role));
}

/**
 * Get all resolved roles for the user in the current tenant.
 */
export function useCurrentRoles(): string[] {
  const { currentTenant } = useTenant();
  return resolveRoles(currentTenant?.role);
}
